<template>
    <div class="fill-height">
        <template v-if="loading && firstInit">
            <div class="d-flex justify-center align-center fill-height">
                <v-progress-circular
                        indeterminate
                        size="64"
                ></v-progress-circular>
            </div>
        </template>
        <template v-else>
            <div v-if="items.length || $route.name === 'alertHistory'">
                <div class="d-flex">
                    <v-text-field
                            v-model="search"
                            class="datatable-search"
                            dense
                            hide-details
                            outlined
                            prepend-inner-icon="mdi-magnify"/>
                </div>
                <v-data-table
                        v-if="items.length"
                        :headers="header"
                        :items="filteredItems"
                        :loading="loadingTable"
                        :search="search"
                        class="rounded-lg shadow my-6 history-table"
                        sort-by="CreatedAt"
                        sort-desc
                        @click:row="showDetails">
                    <template v-slot:[`item.Active`]="{ item }">
                        {{ item.Active ? $t('user.active') : $t('inactive') }}
                    </template>
                    <template v-slot:[`item.Recurring`]="{ item }">
                        {{ item.Recurring ? $t('yes') : $t('no') }}
                    </template>
                    <template v-slot:[`item.Severity`]="{ item }">
                        {{ getSeverity(item.Severity) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <TableAction :tooltip="$t('tooltips.moreDetails')" icon="mdi-information"
                                     @click="showDetails(item)"/>
                    </template>
                    <template v-slot:[`header.Severity`]>
                        <FilterDropDown
                                v-model="filters.severities"
                                :available-values="Array.from(availableValues.severities)"
                                :filter-values="severities"
                                :name="$t('notifications.severity')"/>
                    </template>
                    <template v-slot:[`header.Recurring`]>
                        <FilterDropDown
                                v-model="filters.recurring"
                                :available-values="Array.from(availableValues.recurring)"
                                :filter-values="recurring"
                                :name="$t('alerts.recurring')"/>
                    </template>
                    <template v-slot:[`header.Active`]>
                        <FilterDropDown
                                v-model="filters.statuses"
                                :available-values="Array.from(availableValues.statuses)"
                                :filter-values="statuses"
                                :name="$t('status')"/>
                    </template>
                </v-data-table>
                <AlertScheduledDetails :key="alertID"
                                       :alert="alert"
                                       :alertID="alertID"
                                       :open="openDetails"
                                       @close="openDetails = false"/>
            </div>
            <div v-if="!items.length" class="d-flex align-center justify-center fill-height">
                <img :src="require('@/assets/no-geofence.png')" alt="logo">

                <div v-if="$route.name === 'geofenceHistory'" class="font-weight-bold body-1 ml-5">
                    {{ $t('zones.noHistoryOfGeofenceEvents') }}
                </div>
                <div v-else-if="$route.name === 'circleHistory'" class="font-weight-bold body-1 ml-5">
                    {{ $t('circles.noCircleHistory') }}
                </div>
                <div v-else-if="$route.name === 'scheduledAlerts'" class="font-weight-bold body-1 ml-5">
                    {{ $t('alerts.noScheduledAlerts') }}
                </div>
                <div v-else class="font-weight-bold body-1 ml-5">{{ $t('alerts.noAlertHistory') }}</div>
            </div>
        </template>
    </div>
</template>

<script>
import {formatDateTime} from "@/helper/helper";
import {
  NOTIFICATIONS_SEVERITY_CRITICAL,
  NOTIFICATIONS_SEVERITY_INFO,
  NOTIFICATIONS_SEVERITY_WARNING
} from "@/helper/constants";
import FilterDropDown from "@/components/reusable/FilterDropDown/FilterDropDown";
import AlertScheduledDetails from "@/components/Alert/AlertScheduledDetails";

export default {
  name: "AlertScheduled",
  components: {AlertScheduledDetails, FilterDropDown},
  mounted() {
    this.initAlerts();
    window.addEventListener('keyup', this.handleKeyup);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyup);
  },
  data() {
    return {
      search: '',
      items: [],
      loading: false,
      loadingTable: false,
      filters: {
        severities: [],
        recurring: [],
        statuses: [],
      },
      availableValues: {
        severities: new Set(),
        recurring: new Set(),
        statuses: new Set(),
      },
      openDetails: false,
      alertID: null,
      alert: {},
      senders: [],
      senderMails: [],
      startDate: null,
      endDate: null,
      startDateOnly: null,
      endDateOnly: null,
      firstInit: true,
      showAlert: false,
    };
  },
  computed: {
    maxTime() {
      if (!this.endDate || !this.startDateOnly) {
        return '23:59';
      }
      if (this.sameDay(this.endDate, this.startDateOnly)) {
        return this.endDate.getHours() + ':' + this.endDate.getMinutes();
      }
      return '23:59';
    },
    minTime() {
      if (!this.startDate || !this.endDateOnly) {
        return '00:00';
      }
      if (this.sameDay(this.startDate, this.endDateOnly)) {
        return this.startDate.getHours() + ':' + this.startDate.getMinutes();
      }
      return '00:00';
    },
    showStartToday() {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date()) >= 0;
    },
    showEndToday() {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date()) - this.dateToEpoch(this.startDate) >= 0;
    },
    showStartNow() {
      let maxDate = new Date(),
          maxTime = this.maxTime.split(':');
      maxDate.setHours(maxTime[0]);
      maxDate.setMinutes(maxTime[1]);

      return maxDate.getTime() - new Date().getTime() >= 0;
    },
    showEndNow() {
      let minDate = new Date(),
          minTime = this.minTime.split(':');
      minDate.setHours(minTime[0]);
      minDate.setMinutes(minTime[1]);

      return new Date().getTime() - minDate.getTime() >= 0;
    },
    filteredItems() {
      return this.items.filter(item => this.filters.severities.includes(item.Severity) && this.filters.recurring.includes(item.Recurring) && this.filters.statuses.includes(item.Active));
    },
    header() {
      return [
        {text: this.$t('date'), value: 'CreatedAt'},
        {text: this.$t('notifications.title'), value: 'Title'},
        {text: this.$t('authentication.address'), value: 'Address'},
        {text: this.$t('notifications.severity'), value: 'Severity'},
        {text: this.$t('alerts.recurring'), value: 'Recurring'},
        {text: this.$t('status'), value: 'Active'},
        {text: '', value: 'actions', sortable: false, width: 200},
      ];
    },
    severities() {
      return [
        {
          text: this.$t('notifications.info'),
          value: NOTIFICATIONS_SEVERITY_INFO
        },
        {
          text: this.$t('notifications.warning'),
          value: NOTIFICATIONS_SEVERITY_WARNING
        },
        {
          text: this.$t('notifications.critical'),
          value: NOTIFICATIONS_SEVERITY_CRITICAL
        }
      ];
    },
    recurring() {
      return [
        {
          text: this.$t('yes'),
          value: true
        },
        {
          text: this.$t('no'),
          value: false
        },
      ];
    },
    statuses() {
      return [
        {
          value: true,
          text: this.$t('user.active')
        },
        {
          value: false,
          text: this.$t('inactive')
        },
      ];
    },
    isStartDateToday() {
      return this.sameDay(this.startDate, new Date());
    },
    isEndDateToday() {
      return this.sameDay(this.endDate, new Date());
    }
  },
  methods: {
    formatDateTime,
    initAlerts() {
      this.loading = true;
      this.loadingTable = true;
      let params = {};
      if (this.startDate || this.endDate) {
        params.startDate = this.startDate;
        params.endDate = this.endDate;
      }
      const url = this.$store.getters.scheduledAlerts(this.$store.getters.getUserId);
      this.axios.get(url, {params}).then(({data}) => {
        this.items = data.Result;
        this.items.forEach(item => {
          this.availableValues.severities.add(item.Severity);
          this.availableValues.recurring.add(item.Recurring);
          this.availableValues.statuses.add(item.Active);
          if (this.items.length && this.firstInit) {
            this.showAlert = true;
            setTimeout(() => {
              this.showAlert = false;
            }, 1000 * 10);
            this.firstInit = false;
          }
        });
      }).finally(() => {
        this.loading = false;
        this.loadingTable = false;
      });
    },
    getSeverity(severityID) {
      if (!severityID) {
        return '';
      }
      return this.severities.filter(severity => severity.value === severityID)[0].text;
    },
    showDetails(alert) {
      this.alert = alert;
      this.alertID = alert.AlertID;
      this.openDetails = true;
    },
    allowedStartDates(val) {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date(val)) >= 0;
    },
    dateToEpoch(date) {
      const time = new Date(date);
      time.setHours(0);
      time.setMinutes(0);
      time.setSeconds(0);
      return Math.floor(time.getTime() / 1000);
    },
    allowedEndDates(val) {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date(val)) - this.dateToEpoch(this.startDate) >= 0;
    },
    updateStartDate(val) {
      this.startDateOnly = new Date(val);
    },
    updateEndDate(val) {
      this.endDateOnly = new Date(val);
    },
    sameDay(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getDate() === d2.getDate();
    },
    handleKeyup(e) {
      if (e.key === 'Escape') {
        this.$router.back();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "alert-history";
</style>